import { IonList } from "@ionic/react";
import { ComponentProps, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import MedicationForm from "@components/forms/MedicationForm";
import Modal, { ModalElement } from "@components/Modal";
import TrackedItem from "@components/trackers/TrackedItem";
import TrackedItemLine from "@components/trackers/TrackedItemLine";
import Tracker from "@components/trackers/Tracker";
import styles from "@components/trackers/Tracker.module.css";
import TrackerItemModal from "@components/trackers/TrackerItemModal";
import useDestroyMedication from "@hooks/mutations/useDestroyMedication";
import useOpener from "@hooks/useOpener";
import { GetJourneyWithPlanQuery, MedicationFormatEnum, MedicationFragment } from "@typing/Generated";
import { sortByTimestamp } from "@utils/modelUtils";

type Props = {
  journey: GetJourneyWithPlanQuery["journey"];
};

type MedicationProps = {
  handleDestroy: (medication: MedicationFragment) => void;
  handleEdit: (medication: MedicationFragment) => void;
  medication: MedicationFragment;
};

const Medication = ({ handleDestroy, handleEdit, medication }: MedicationProps) => {
  const opener = useOpener();
  const intl = useIntl();

  const itemProps: ComponentProps<typeof TrackedItemLine>[] = [];
  if (medication.reasonForTaking) {
    itemProps.push({ description: medication.reasonForTaking, labelKey: "pages.client.medications.list.indication" });
  }
  if (medication.dosage) {
    itemProps.push({ description: medication.dosage, labelKey: "pages.client.medications.list.dosageAndFrequency" });
  }
  if (medication.instructions) {
    itemProps.push({ description: medication.instructions, labelKey: "pages.client.medications.list.instructions" });
  }
  if (medication.format.formatOption) {
    itemProps.push({
      description:
        medication.format.formatOption === MedicationFormatEnum.OTHER && medication.format.other
          ? medication.format.other
          : medication.format.formatOption,
      labelKey: "pages.client.medications.list.format"
    });
  }
  if (medication.prescriber) {
    itemProps.push({ description: medication.prescriber, labelKey: "pages.client.medications.list.prescriber" });
  }
  if (medication.refillFrequency) {
    itemProps.push({
      description: medication.refillFrequency,
      labelKey: "pages.client.medications.list.refillFrequency"
    });
  }
  if (medication.pharmacy) {
    itemProps.push({ description: medication.pharmacy, labelKey: "pages.client.medications.list.pharmacy" });
  }
  if (medication.prescriptionNumber) {
    itemProps.push({
      description: medication.prescriptionNumber,
      labelKey: "pages.client.medications.list.prescriptionNumber"
    });
  }
  if (medication.paymentType) {
    itemProps.push({
      description: intl.formatMessage({
        id: `pages.client.medications.list.paymentTypeOptions.${medication.paymentType}`
      }),
      labelKey: "pages.client.medications.list.paymentType"
    });
  }

  return (
    <>
      <TrackedItem
        onClick={opener.open}
        subtitle={intl.formatMessage({ id: `models.medication.kindOptions.${medication.kind}` })}
        title={medication.name}
      />
      <TrackerItemModal
        actions={[
          {
            handler: () => handleEdit(medication),
            icon: "edit",
            labelKey: "dictionary.edit"
          },
          {
            handler: () => handleDestroy(medication),
            icon: "trash",
            labelKey: "dictionary.remove"
          }
        ]}
        notes={medication.notes}
        opener={opener}
        subtitle={intl.formatMessage({ id: `models.medication.kindOptions.${medication.kind}` })}
        title={medication.name}
      >
        <IonList className="ion-no-padding">
          {itemProps.map((props, index) => (
            // array index key is actually fine here since the array is static and it's the most convenient thing to use
            // eslint-disable-next-line react/no-array-index-key
            <TrackedItemLine key={index} {...props} color={index % 2 === 0 ? "neutral" : "white"} />
          ))}
        </IonList>
      </TrackerItemModal>
    </>
  );
};

const Medications = ({ journey }: Props) => {
  const intl = useIntl();
  const addOpener = useOpener();
  const medicationModalRef = useRef<ModalElement>(null);
  const medicalRef = useRef<HTMLDivElement>(null);
  const [editingMedication, setEditingMedication] = useState<MedicationFragment | undefined>(undefined);

  useEffect(() => {
    if (location.hash.includes("medication") && medicalRef.current) {
      medicalRef.current.scrollIntoView({ behavior: "smooth" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleMedicationError = () => {
    if (medicationModalRef.current) {
      medicationModalRef.current.scrollToTop();
    }
  };

  const destroyMedication = useDestroyMedication(journey.id);

  return (
    <div className={styles.trackerContainer} ref={medicalRef}>
      <Tracker
        handleAdd={() => {
          setEditingMedication(undefined);
          addOpener.open();
        }}
        kind="medical"
      >
        <div className={styles.items}>
          {sortByTimestamp(journey.medications, medication => medication.createdAt).map(medication => (
            <Medication
              handleDestroy={m => {
                destroyMedication({
                  id: m.id
                });
              }}
              handleEdit={m => {
                setEditingMedication(m);
                addOpener.open();
              }}
              key={medication.id}
              medication={medication}
            />
          ))}
        </div>
      </Tracker>
      <Modal
        opener={addOpener}
        ref={medicationModalRef}
        title={
          editingMedication
            ? intl.formatMessage({ id: "models.client.medication.updateMedication" })
            : intl.formatMessage({ id: "models.client.medication.addNewMedication" })
        }
      >
        {({ modalDisplayed }) => (
          <MedicationForm
            autofocus={modalDisplayed}
            journeyId={journey.id}
            medication={editingMedication}
            onCancel={addOpener.close}
            onError={handleMedicationError}
            onSuccess={addOpener.close}
          />
        )}
      </Modal>
    </div>
  );
};

export default Medications;
